import { useState, useCallback } from 'react';

/**
 * Hook for managing selection state in library and trash views
 */
export const useLibrarySelection = () => {
  // Selected folder in tree
  const [selectedFolder, setSelectedFolder] = useState(null);
  
  // Expanded folders in tree
  const [expandedFolders, setExpandedFolders] = useState(new Set(['root']));
  
  // Selected trash items (document IDs)
  const [selectedTrashItems, setSelectedTrashItems] = useState(new Set());

  // Toggle folder expansion
  const toggleFolderExpand = useCallback((folderId) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  }, []);

  // Select folder
  const selectFolder = useCallback((folder) => {
    setSelectedFolder(folder);
    if (folder?.id) {
      setExpandedFolders(prev => new Set([...prev, folder.id]));
    }
  }, []);

  // Clear folder selection
  const clearFolderSelection = useCallback(() => {
    setSelectedFolder(null);
  }, []);

  // Toggle single trash item selection
  const toggleTrashSelection = useCallback((itemId) => {
    setSelectedTrashItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  }, []);

  // Select all trash documents
  const selectAllTrash = useCallback((documentIds) => {
    setSelectedTrashItems(new Set(documentIds));
  }, []);

  // Clear trash selection
  const clearTrashSelection = useCallback(() => {
    setSelectedTrashItems(new Set());
  }, []);

  // Toggle select all trash
  const toggleSelectAllTrash = useCallback((allDocumentIds) => {
    if (selectedTrashItems.size === allDocumentIds.length) {
      clearTrashSelection();
    } else {
      selectAllTrash(allDocumentIds);
    }
  }, [selectedTrashItems.size, clearTrashSelection, selectAllTrash]);

  // Check if all are selected
  const isAllTrashSelected = useCallback((totalCount) => {
    return selectedTrashItems.size === totalCount && totalCount > 0;
  }, [selectedTrashItems.size]);

  return {
    // Folder selection
    selectedFolder,
    selectFolder,
    clearFolderSelection,
    
    // Folder expansion
    expandedFolders,
    toggleFolderExpand,
    setExpandedFolders,

    // Trash selection
    selectedTrashItems,
    toggleTrashSelection,
    selectAllTrash,
    clearTrashSelection,
    toggleSelectAllTrash,
    isAllTrashSelected,
    selectedTrashCount: selectedTrashItems.size,
  };
};

export default useLibrarySelection;
